import { useMemo, useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import NoData from '@bumble/app/components/NoData';
import { TabGrid, TabGridStructure } from '@bumble/app/components/TabGrid';
import { theme } from '@bumble/app/theme';
import { TicketDetail, Correspondence, TicketFile } from '@bumble/app/__generated__/Api';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { CorrespondenceDialog } from '@bumble/app/components/support-cases';

const accordionStyles = {
  boxShadow: 'none',
  '&::before': { display: 'none' },
  '&:not(:last-of-type)': { mb: 0.5 },
};

const accordionSummaryStyles = {
  flexDirection: 'row-reverse',
  fontWeight: 700,
  minHeight: 36,
  '&.Mui-expanded': { minHeight: 36 },
  '& .MuiAccordionSummary-content': { my: 0 },
  '& .MuiAccordionSummary-content.Mui-expanded': { my: 0 },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)',
    color: theme.palette.primary.main,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)',
  },
};

const AttachmentList = ({ files }: { files: TicketFile[] }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
    {files.map((file) => (
      <Chip
        key={file.object_key}
        icon={<AttachFileIcon fontSize="small" />}
        label={file.filename}
        size="small"
        variant="outlined"
        clickable={!!file.download_url}
        component={file.download_url ? 'a' : 'div'}
        href={file.download_url}
        target="_blank"
        rel="noopener noreferrer"
      />
    ))}
  </Box>
);

interface CaseDetailsProps {
  data?: TicketDetail | null;
  onReplySent: () => void;
  onRefetchDetail: () => void;
}

export const CaseDetails = ({ data, onReplySent, onRefetchDetail  }: CaseDetailsProps) => {
  if (!data) return <NoData />;

  const [showCorrespondenceDialog, setShowCorrespondenceDialog] = useState(false);
  const [isReplying, setIsReplying] = useState(false);

  const tabDataGeneral: TabGridStructure[] = [
    { label: 'Title', content: data.title || 'N/A' },
    { label: 'Category', content: data.category || 'N/A' },
    { label: 'Priority', content: data.priority || 'N/A' },
    { label: 'Status', content: data.state || 'N/A' },
    { label: 'Ticket ID', content: data.ticket_id || 'N/A' },
    { label: 'Account ID', content: data.account_id || 'N/A' },
    { label: 'Device ID', content: data.device_id ?? 'N/A' },
    { label: 'Entity ID', content: data.entity_id ?? 'N/A' },
    { label: 'Created by', content: data.created_by || 'N/A' },
    { label: 'Created at', content: data.created_at || 'N/A' },
    { label: 'Updated at', content: data.updated_at || 'N/A' },
    ...(data.uploaded_files?.length
      ? [{ label: 'Attachments', content: <AttachmentList files={data.uploaded_files} /> }]
      : []),
  ];

  const correspondenceList: Correspondence[] = useMemo(() => {
    const arr = Array.isArray(data?.correspondence) ? [...data.correspondence] : [];
    arr.sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
    return arr;
  }, [data]);

  const openReply = () => {
    onRefetchDetail();
    setIsReplying(true);
    setShowCorrespondenceDialog(true);
  };

  const closeDialog = () => {
    setShowCorrespondenceDialog(false);
    setIsReplying(false);
  };

  return (
    <Box>
      <Accordion sx={accordionStyles}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
          General
        </AccordionSummary>
        <AccordionDetails>
          <TabGrid data={tabDataGeneral} />
        </AccordionDetails>
      </Accordion>

      <Accordion sx={accordionStyles} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
          Correspondence
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" onClick={openReply} sx={{backgroundColor: '#EABF4E',}}>
              Reply
            </Button>
          </Box>

          {correspondenceList.length === 0 ? (
            <NoData />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {correspondenceList.map((c, idx) => (
                <Box
                  key={c.correspondence_id ?? idx}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '240px 1fr' },
                    gap: 2,
                    alignItems: 'start',
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {c.created_by ?? 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      {c.created_at ?? 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {c.correspondence ?? '—'}
                    </Typography>
                    {!!c.uploaded_files?.length && (
                      <Box sx={{ mt: 1 }}>
                        <AttachmentList files={c.uploaded_files} />
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {showCorrespondenceDialog && data.ticket_id && (
        <DialogStandard
          open={showCorrespondenceDialog}
          handleClose={closeDialog}
          title="Correspondence"
        >
          <CorrespondenceDialog
            ticketID={data.ticket_id}
            isReplying={isReplying}
            onReplySent={() => {
              closeDialog();
              onReplySent();
            }}
            onClose={closeDialog}
          />
        </DialogStandard>
      )}
    </Box>
  );
};

export default CaseDetails;
